import { useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronUp } from "lucide-react";
import { isInboundSms } from "@/components/comms/DirectionBadge";

// Two-line preview; click / Enter toggles full text inline.
export const CommMessageCell = ({ comm, testId }) => {
    const body = comm?.body || "";
    const inbound = isInboundSms(comm);
    const textRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [overflows, setOverflows] = useState(false);

    useLayoutEffect(() => {
        const el = textRef.current;
        if (!el || open) return;
        const check = () => setOverflows(el.scrollHeight > el.clientHeight + 1);
        check();
        const ro = new ResizeObserver(check);
        ro.observe(el);
        return () => ro.disconnect();
    }, [body, open]);

    const toggle = () => { if (overflows || open) setOpen(o => !o); };
    const interactive = overflows || open;

    return (
        <div data-testid={testId}>
            <div
                role={interactive ? "button" : undefined}
                tabIndex={interactive ? 0 : undefined}
                aria-expanded={interactive ? open : undefined}
                onClick={toggle}
                onKeyDown={e => { if (interactive && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); toggle(); } }}
                className={[
                    "text-xs whitespace-pre-wrap break-words [overflow-wrap:anywhere] rounded-md px-2 py-1 outline-none",
                    inbound ? "text-gray-900 bg-emerald-50 border-l-2 border-emerald-400" : "text-gray-700",
                    interactive ? "cursor-pointer hover:bg-gray-100/70 focus-visible:ring-2 focus-visible:ring-primary/40" : "",
                ].join(" ")}
                data-testid={`${testId}-text`}
            >
                <div ref={textRef} className={open ? "" : "line-clamp-2"}>{body || (inbound ? "(empty message)" : "—")}</div>
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-3 px-2">
                {inbound && comm.from_phone ? (
                    <span className="text-[10px] font-mono text-gray-500" data-testid={`${testId}-from`}>from {comm.from_phone}</span>
                ) : null}
                {interactive ? (
                    <button type="button" onClick={toggle} className="inline-flex items-center gap-0.5 text-[11px] font-medium text-primary hover:underline" data-testid={`${testId}-toggle`}>
                        {open ? <>Show less <ChevronUp className="h-3 w-3" /></> : <>Show more <ChevronDown className="h-3 w-3" /></>}
                    </button>
                ) : null}
            </div>
        </div>
    );
};

export const commDetailItems = (comm, campaign) => {
    const snap = comm?.snapshot || {};
    const campLabel = snap.campaign_name || campaign?.name;
    return [
        campLabel && { k: "Campaign", v: campaign ? <Link to={`/campaigns/${campaign.id}`} className="text-primary hover:underline">{campLabel}</Link> : campLabel },
        (snap.template_key || snap.template_name) && { k: "Template", v: snap.template_key || snap.template_name },
        comm?.provider_status && { k: "Provider", v: comm.provider_status },
        (comm?.error_code || comm?.error_message) && { k: "Error", v: [comm.error_code, comm.error_message].filter(Boolean).join(" — ") },
    ].filter(Boolean);
};

export const CommDetails = ({ items, testId }) => {
    const [open, setOpen] = useState(false);
    if (!items.length) return null;
    return (
        <div className="mt-1 px-2" data-testid={testId}>
            <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open} className="inline-flex items-center gap-0.5 text-[11px] text-gray-500 hover:text-gray-800" data-testid={`${testId}-toggle`}>
                Details {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </button>
            {open ? (
                <dl className="mt-1 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 rounded-md bg-gray-50 border border-border px-2 py-1.5 text-[11px]" data-testid={`${testId}-panel`}>
                    {items.map(i => (
                        <div key={i.k} className="contents">
                            <dt className="text-gray-500">{i.k}</dt>
                            <dd className="text-gray-800 break-words [overflow-wrap:anywhere]">{i.v}</dd>
                        </div>
                    ))}
                </dl>
            ) : null}
        </div>
    );
};
